/**
 * llms.txt Page (Spec 080)
 *
 * Per-brand view for generating, previewing, downloading, and versioning
 * an llms.txt markdown file, plus fetching the brand's currently-served
 * /llms.txt and diffing against the latest generated version.
 */

import { useState, useEffect, useCallback, useMemo } from 'react';
import type { ReactNode } from 'react';
import { useBrand } from '../lib/brand-context.js';
import { apiFetch, apiGet } from '../lib/api.js';
import { EmptyState } from '../components/EmptyState.js';
import { Icon, Spinner } from '../components/ui/Icon.js';
import { Button } from '../components/ui/Button.js';
import { Chip } from '../components/ui/Chip.js';
import { Banner } from '../components/ui/Banner.js';
import { Link } from 'react-router';
import { Section, SectionHead } from '../components/ui/Section.js';
import { Select } from '../components/Select.js';
import type {
  BrandLlmsTxt,
  LlmsTxtCoverage,
  LlmsTxtGenerateResponse,
  LlmsTxtListResponse,
  LlmsTxtFetchServedResponse,
} from '../types/index.js';

function formatRelativeTime(iso: string | null | undefined): string {
  if (!iso) return 'never';
  const now = Date.now();
  const t = new Date(iso).getTime();
  const diff = Math.max(0, Math.round((now - t) / 1000));
  if (diff < 60) return `${diff}s ago`;
  if (diff < 3600) return `${Math.round(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.round(diff / 3600)}h ago`;
  return `${Math.round(diff / 86400)}d ago`;
}

const STAGE_LABELS = [
  'Collecting URLs',
  'Fetching metadata',
  'Synthesizing',
  'Validating',
] as const;

// Client-side stage pacing (ms) — optimistic, not backend-driven.
const STAGE_TIMINGS = [800, 14000, 16000, 800];

export function LlmsTxtPage() {
  const { selectedBrand, selectedBrandTenantId } = useBrand();
  const tenantId = selectedBrandTenantId;
  const brandId = selectedBrand?.id ?? null;

  const [versions, setVersions] = useState<BrandLlmsTxt[]>([]);
  const [selectedVersionId, setSelectedVersionId] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [fetchingServed, setFetchingServed] = useState(false);
  const [stage, setStage] = useState(0);
  const [coverage, setCoverage] = useState<LlmsTxtCoverage | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [toast, setToast] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);
  const [servedResult, setServedResult] = useState<
    | { served: BrandLlmsTxt; diff: string }
    | { served: null; reason: string }
    | null
  >(null);
  const [showDiff, setShowDiff] = useState(false);

  // ── Loaders ─────────────────────────────────────────────────────
  const loadVersions = useCallback(async () => {
    if (!brandId || !tenantId) return;
    setLoading(true);
    setError(null);
    try {
      const result = await apiGet<LlmsTxtListResponse>(
        `/api/v1/tenants/${tenantId}/brands/${brandId}/llms-txt`,
      );
      setVersions(result.data);
      // Prefer showing the latest generated version on first load.
      const latestGenerated = result.data.find((v) => v.kind === 'llms_txt');
      if (latestGenerated) {
        setSelectedVersionId((prev) => prev ?? latestGenerated.id);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load versions');
    } finally {
      setLoading(false);
    }
  }, [brandId, tenantId]);

  useEffect(() => {
    setVersions([]);
    setSelectedVersionId(null);
    setCoverage(null);
    setServedResult(null);
    setShowDiff(false);
    if (brandId && tenantId) void loadVersions();
  }, [brandId, tenantId, loadVersions]);

  // Auto-clear toast
  useEffect(() => {
    if (!toast) return;
    const t = setTimeout(() => setToast(null), 4000);
    return () => clearTimeout(t);
  }, [toast]);

  // Client-side progress pacing — pure UX, no backend polling.
  useEffect(() => {
    if (!generating) return;
    setStage(0);
    let cancelled = false;
    const timers: ReturnType<typeof setTimeout>[] = [];
    let acc = 0;
    for (let i = 0; i < STAGE_TIMINGS.length - 1; i++) {
      acc += STAGE_TIMINGS[i];
      timers.push(
        setTimeout(() => {
          if (!cancelled) setStage(i + 1);
        }, acc),
      );
    }
    return () => {
      cancelled = true;
      timers.forEach(clearTimeout);
    };
  }, [generating]);

  // ── Actions ─────────────────────────────────────────────────────
  async function handleGenerate() {
    if (!brandId || !tenantId || !selectedBrand) return;
    setGenerating(true);
    setError(null);
    setShowDiff(false);
    try {
      const res = await apiFetch(
        `/api/v1/tenants/${tenantId}/brands/${brandId}/llms-txt/generate`,
        { method: 'POST', body: JSON.stringify({}) },
      );
      if (!res.ok) {
        const body = (await res.json().catch(() => ({}))) as { error?: string };
        throw new Error(body.error ?? `Generation failed (${res.status})`);
      }
      const body = (await res.json()) as LlmsTxtGenerateResponse;
      setCoverage(body.data.coverage);
      setToast(`Generated version ${body.data.version}`);
      setSelectedVersionId(body.data.id);
      await loadVersions();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Generation failed');
    } finally {
      setGenerating(false);
      setStage(0);
    }
  }

  async function handleFetchServed() {
    if (!brandId || !tenantId) return;
    setFetchingServed(true);
    setError(null);
    try {
      const res = await apiFetch(
        `/api/v1/tenants/${tenantId}/brands/${brandId}/llms-txt/fetch-served`,
        { method: 'POST', body: JSON.stringify({}) },
      );
      if (!res.ok) {
        const body = (await res.json().catch(() => ({}))) as { error?: string };
        throw new Error(body.error ?? `Fetch failed (${res.status})`);
      }
      const body = (await res.json()) as LlmsTxtFetchServedResponse;
      setServedResult(body.data);
      if (body.data.served) {
        setToast('Fetched served /llms.txt');
        setShowDiff(true);
        await loadVersions();
      } else {
        setToast('No /llms.txt currently served at this domain');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Fetch failed');
    } finally {
      setFetchingServed(false);
    }
  }

  async function handleDownload() {
    if (!brandId || !tenantId || !selectedVersionId) return;
    try {
      const res = await apiFetch(
        `/api/v1/tenants/${tenantId}/brands/${brandId}/llms-txt/${selectedVersionId}/download`,
      );
      if (!res.ok) throw new Error(`Download failed (${res.status})`);
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'llms.txt';
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Download failed');
    }
  }

  async function handleCopy(text: string) {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      setError('Copy failed');
    }
  }

  // ── Derived state ───────────────────────────────────────────────
  const selectedVersion = useMemo(
    () => versions.find((v) => v.id === selectedVersionId) ?? null,
    [versions, selectedVersionId],
  );

  const generatedVersions = useMemo(
    () => versions.filter((v) => v.kind === 'llms_txt'),
    [versions],
  );

  const latestServed = useMemo(
    () => versions.find((v) => v.kind === 'served') ?? null,
    [versions],
  );

  if (!selectedBrand) {
    return (
      <div className="hub-page--reading space-y-6">
        <div className="hub-page__head">
          <h1 className="hub-page__title">llms.txt</h1>
        </div>
        <EmptyState
          icon="bx-file"
          title="Select a brand"
          description="Choose a brand to generate its llms.txt file."
        />
      </div>
    );
  }

  return (
    /* Spec 236 — the READING archetype, and the assignment was always
       obvious: this page is a document. A generated file, the diff against
       what the brand actually publishes, and the history of both. The root
       constrains only `p` and `li` to prose width (layout.css), which is why
       the side-by-side comparison and the history table below still run the
       full width of the page without fighting it.

       `space-y-6` on the root is the convention every reading page shipped by
       spec 233 uses (RuleDetailPage, FanOutClusterDetailPage, the two page-
       evaluation pages) — sections are spaced, not hairlined, on this
       archetype. */
    <div data-testid="llms-txt-page" className="hub-page--reading space-y-6">
      <div className="hub-page__head">
        <div>
          <h1 className="hub-page__title">llms.txt</h1>
          <p className="mt-1 text-sm text-[var(--hub-color-ink-muted)]">
            Generate a spec-compliant llms.txt for {selectedBrand.name}
            {selectedBrand.domain ? ` (${selectedBrand.domain})` : ''} using
            GEO Observatory's ranking, citation, and topic data.
          </p>
        </div>
      </div>

      {/* Evidence-state banner — see Change 1 in inputs/improvements.md.
          Surfaces the current evidence base for llms.txt so consultants and
          clients aren't using the feature under the wrong epistemic frame. */}
      <div
        className="border border-amber-200 bg-amber-50/70 p-4 text-sm text-amber-900"
        data-testid="llms-txt-evidence-banner"
      >
        <div className="flex gap-3">
          <Icon name="bx-error" className="mt-0.5 shrink-0 text-amber-700" />
          <div>
            <div className="flex flex-wrap items-center gap-2">
              <Chip tone="warn">Weak evidence</Chip>
              <span className="font-semibold">Important context on llms.txt impact</span>
            </div>
            <p className="mt-2 leading-relaxed">
              Google has officially stated (May 2026) that llms.txt files do
              not influence visibility in Google's AI surfaces (AI Overviews,
              AI Mode, Gemini). Independent analysis of 54 GEO studies has
              not found a measurable effect on other major engines either.
            </p>
            <p className="mt-2 leading-relaxed">
              GEO Observatory continues to support llms.txt generation and hosting
              because (a) other AI tools may consume these files in ways not
              yet publicly documented, and (b) GEO Observatory's Markdown Fetch
              Monitoring is positioned to detect any future effect. Treat
              llms.txt deployment as a <strong>low-cost speculative
              intervention</strong>, not a high-confidence optimization.
            </p>
          </div>
        </div>
      </div>

      {/* Spec 249 (review P1-3): the reason sits above the actions it
          explains, as a Banner that links to the fix; the actions keep their
          own style, aria-disabled, rather than greying out. */}
      {!selectedBrand.domain && (
        <Banner tone="warning" title="This brand has no domain set" testId="llms-txt-no-domain">
          <span id="llms-txt-no-domain-reason">llms.txt is generated for a domain, so generating and fetching are unavailable until one is set.</span>{' '}
          <Link to="/settings">Add a domain</Link>
        </Banner>
      )}

      {/* Action bar. `.hub-toolbar` puts the filters and actions directly
          above the thing they act on, and `Button`/`Select` replace the hand-
          rolled controls — including the native select element, which Kridt
          has no recipe for and which spec 232 removed from every page it
          touched. */}
      <div className="hub-toolbar">
        <Button
          variant="primary"
          onClick={handleGenerate}
          disabled={generating}
          aria-disabled={!selectedBrand.domain || undefined}
          aria-describedby={!selectedBrand.domain ? 'llms-txt-no-domain-reason' : undefined}
          data-testid="llms-txt-generate"
        >
          {generating ? <Spinner  /> : <Icon name="bx-star" />}
          Generate
        </Button>
        <Button
          variant="ghost"
          onClick={handleFetchServed}
          disabled={fetchingServed}
          aria-disabled={!selectedBrand.domain || undefined}
          aria-describedby={!selectedBrand.domain ? 'llms-txt-no-domain-reason' : undefined}
          data-testid="llms-txt-fetch-served"
        >
          {fetchingServed ? <Spinner  /> : <Icon name="bx-cloud-download" />}
          Fetch served
        </Button>

        {generatedVersions.length > 0 && (
          <Select
            options={versions.map((v) => ({
              value: v.id,
              label: `${v.kind === 'served' ? 'Served' : `Version ${v.version}`} — ${formatRelativeTime(v.generated_at)}`,
            }))}
            value={selectedVersionId ?? ''}
            onChange={(v) => setSelectedVersionId(v || null)}
            placeholder="Select a version"
            className="w-72"
            data-testid="llms-txt-version-select"
          />
        )}
      </div>

      {/* Coverage banner. Tone from spec 235's mapping: amber → warn,
          red → fail, and the chip always carries the word as well as the
          colour. */}
      {coverage && coverage.level !== 'full' && (
        <div
          className={`flex items-start gap-2 border px-3 py-2 text-sm ${
            coverage.level === 'partial'
              ? 'border-amber-200 bg-amber-50 text-amber-900'
              : 'border-red-200 bg-red-50 text-red-900'
          }`}
          data-testid="llms-txt-coverage-banner"
        >
          <Chip tone={coverage.level === 'partial' ? 'warn' : 'fail'}>
            Coverage: {coverage.level}
          </Chip>
          {coverage.reason ? <span className="mt-0.5">{coverage.reason}</span> : null}
        </div>
      )}

      {/* Progress indicator while generating */}
      {generating && (
        <div className="border border-violet-200 bg-violet-50/60 p-4">
          <div className="text-sm font-medium text-violet-900">Generating llms.txt…</div>
          <ol className="mt-3 space-y-1.5">
            {STAGE_LABELS.map((label, i) => {
              const state =
                i < stage ? 'done' : i === stage ? 'active' : 'pending';
              return (
                <li
                  key={label}
                  className={`flex items-center gap-2 text-sm ${
                    state === 'done'
                      ? 'text-emerald-700'
                      : state === 'active'
                        ? 'text-violet-700 font-medium'
                        : 'text-gray-400'
                  }`}
                >
                  {state === 'done' ? (
                    <Icon name="bx-check" />
                  ) : state === 'active' ? (
                    <Spinner  />
                  ) : (
                    <span className="inline-block h-3 w-3 rounded-full border border-gray-300" />
                  )}
                  Step {i + 1}: {label}
                </li>
              );
            })}
          </ol>
        </div>
      )}

      {toast && (
        <div className="flex flex-wrap items-center gap-2 border border-emerald-200 bg-emerald-50 px-4 py-2 text-sm text-emerald-800">
          <Chip tone="ok">Done</Chip>
          {toast}
        </div>
      )}
      {error && (
        <div className="flex flex-wrap items-center gap-2 border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
          <Chip tone="fail">Error</Chip>
          {error}
        </div>
      )}

      {/* Main layout: preview + raw */}
      {loading && (
        <div className="flex justify-center py-8">
          <Spinner className="text-violet-600" />
        </div>
      )}

      {!loading && versions.length === 0 && !generating && (
        <EmptyState
          icon="bx-file"
          title="No llms.txt generated yet"
          description="Click Generate to build a spec-compliant llms.txt from this brand's data."
        />
      )}

      {!loading && selectedVersion && (
        <>
          {/* SPEC 236 §3 — THIS GRID STAYS. `lg:grid-cols-2` is not
              decoration here: the rendered document and its source sit side
              by side so the reader can compare them at a glance, and the
              columns are the only expression of that. Flattening it to a
              stack would destroy the comparison, which is exactly the mistake
              spec 235 made on ComparisonPage and had to revert.

              What changes is the chrome, not the shape: the two halves stop
              being cards floating in a gap and become two panels of one sheet
              divided by a hairline. */}
          <div
            className="grid grid-cols-1 border border-[var(--hub-color-line)] lg:grid-cols-2"
            data-testid="llms-txt-preview"
          >
            {/* Rendered preview */}
            <div className="min-w-0 p-5">
              <div className="mb-3 flex items-center justify-between">
                <h2 className="text-sm font-semibold text-[var(--hub-color-ink-muted)]">
                  Preview
                </h2>
                <span className="text-xs text-[var(--hub-color-ink-muted)]">
                  {selectedVersion.kind === 'served' ? 'served (fetched)' : `v${selectedVersion.version}`}
                </span>
              </div>
              <div
                className="prose prose-sm max-w-none text-[var(--hub-color-ink)]"
                dangerouslySetInnerHTML={{ __html: renderMarkdown(selectedVersion.content) }}
              />
            </div>

            {/* Raw markdown — the hairline is the divider the gap used to be. */}
            <div className="min-w-0 border-t border-[var(--hub-color-line)] p-5 lg:border-l lg:border-t-0">
              <div className="mb-3 flex items-center justify-between gap-2">
                <h2 className="text-sm font-semibold text-[var(--hub-color-ink-muted)]">
                  Raw markdown
                </h2>
                <div className="flex gap-2">
                  <Button
                    variant="ghost"
                    onClick={() => handleCopy(selectedVersion.content)}
                    data-testid="llms-txt-copy"
                  >
                    {copied ? <Icon name="bx-check" /> : <Icon name="bx-copy" />}
                    {copied ? 'Copied' : 'Copy'}
                  </Button>
                  <Button variant="ghost" onClick={handleDownload} data-testid="llms-txt-download">
                    <Icon name="bx-download" />
                    Download
                  </Button>
                </div>
              </div>
              <pre className="max-h-[520px] overflow-auto whitespace-pre-wrap break-words bg-[var(--hub-color-surface-sunken)] p-3 text-xs text-[var(--hub-color-ink)]">
                {selectedVersion.content}
              </pre>
            </div>
          </div>

          {/* Where to publish it */}
          {selectedBrand.domain && (
            <div className="flex flex-wrap items-start gap-2 border border-sky-200 bg-sky-50 px-3 py-2 text-sm text-sky-900">
              <Chip tone="info">Publish at</Chip>
              <div>
                Serve this file at{' '}
                <code className="bg-[var(--hub-color-surface)] px-1 py-0.5 font-mono text-xs">
                  https://{selectedBrand.domain}/llms.txt
                </code>{' '}
                to expose it to LLMs. Click “Fetch served” to compare against what you currently publish.
              </div>
            </div>
          )}

          {/* Served-vs-generated diff */}
          {showDiff && servedResult && servedResult.served && 'diff' in servedResult && (
            <Section data-testid="llms-txt-diff">
              <SectionHead
                title="Served vs generated"
                right={
                  <Button variant="quiet" onClick={() => setShowDiff(false)}>
                    Hide
                  </Button>
                }
              />
              <pre className="max-h-[400px] overflow-auto whitespace-pre-wrap break-words bg-[var(--hub-color-surface-sunken)] p-3 text-xs leading-5">
                {formatDiff(servedResult.diff)}
              </pre>
            </Section>
          )}
          {servedResult && servedResult.served === null && (
            <div
              className="flex flex-wrap items-start gap-2 border border-[var(--hub-color-line)] bg-[var(--hub-color-surface-sunken)] px-4 py-3 text-sm text-[var(--hub-color-ink)]"
              data-testid="llms-txt-not-served"
            >
              <Chip>Not served</Chip>
              <div>
                No llms.txt is currently served at{' '}
                <code className="bg-[var(--hub-color-surface)] px-1 py-0.5 font-mono text-xs">
                  https://{selectedBrand.domain}/llms.txt
                </code>{' '}
                ({servedResult.reason.replace(/_/g, ' ')}). Download the generated file and publish it to enable comparison.
              </div>
            </div>
          )}
          {latestServed && !showDiff && servedResult === null && (
            <div className="text-xs text-[var(--hub-color-ink-muted)]">
              A previously fetched served file exists ({formatRelativeTime(latestServed.generated_at)}). Click Fetch served to refresh and diff.
            </div>
          )}

          {/* Version history. The table keeps `hub-table` and runs the full
              width of the page — `hub-page--reading` clamps only `p` and
              `li`, so there is no conflict between the reading archetype and
              a six-column table. */}
          <Section>
            <SectionHead
              title="History"
              right={
                <span className="text-xs text-[var(--hub-color-ink-muted)]">
                  {versions.length} version{versions.length === 1 ? '' : 's'}
                </span>
              }
            />
            <div className="overflow-x-auto">
              <table className="hub-table" data-testid="llms-txt-history">
                <thead>
                  <tr>
                    <th>Version</th>
                    <th>Kind</th>
                    <th>Generated</th>
                    <th>Model</th>
                    <th className="is-num">Cost (USD)</th>
                    <th className="text-right">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {versions.map((v) => (
                    <tr key={v.id} className={v.id === selectedVersionId ? 'bg-[var(--hub-color-action-soft)]' : undefined}>
                      <td className="font-medium">v{v.version}</td>
                      <td>
                        <Chip tone={v.kind === 'served' ? 'info' : undefined}>{v.kind}</Chip>
                      </td>
                      <td className="text-xs text-[var(--hub-color-ink-muted)]">
                        {formatRelativeTime(v.generated_at)}
                      </td>
                      <td className="text-xs text-[var(--hub-color-ink-muted)]">
                        {v.generator_model ?? '--'}
                      </td>
                      <td className="is-num">
                        {v.token_cost_usd != null ? `$${Number(v.token_cost_usd).toFixed(4)}` : '--'}
                      </td>
                      <td className="text-right text-xs">
                        <Button variant="quiet" onClick={() => setSelectedVersionId(v.id)}>
                          View
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Section>
        </>
      )}
    </div>
  );
}

// ── Tiny markdown renderer ──────────────────────────────────────────
// Intentionally minimal — the generated file has a very predictable shape
// (H1, blockquote, H2s, bulleted links with optional descriptions). Pulls
// in zero dependencies. Escapes HTML and only permits safe constructs.

function escapeHtml(s: string): string {
  return s
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

function renderMarkdown(md: string): string {
  const lines = md.split(/\r?\n/);
  const out: string[] = [];
  let inList = false;

  const flushList = () => {
    if (inList) {
      out.push('</ul>');
      inList = false;
    }
  };

  for (const raw of lines) {
    const line = raw;
    if (!line.trim()) {
      flushList();
      continue;
    }

    if (/^#\s+/.test(line)) {
      flushList();
      out.push(`<h1 class="text-xl font-semibold mt-0 mb-2">${escapeHtml(line.replace(/^#\s+/, ''))}</h1>`);
      continue;
    }
    if (/^##\s+/.test(line)) {
      flushList();
      out.push(`<h2 class="text-base font-semibold mt-4 mb-1">${escapeHtml(line.replace(/^##\s+/, ''))}</h2>`);
      continue;
    }
    if (/^>\s+/.test(line)) {
      flushList();
      out.push(`<blockquote class="border-l-4 border-violet-300 pl-3 text-gray-700 my-2">${escapeHtml(line.replace(/^>\s+/, ''))}</blockquote>`);
      continue;
    }
    if (/^[-*]\s+/.test(line)) {
      if (!inList) {
        out.push('<ul class="list-disc pl-5 space-y-1">');
        inList = true;
      }
      out.push(`<li>${renderInlineMd(line.replace(/^[-*]\s+/, ''))}</li>`);
      continue;
    }

    flushList();
    out.push(`<p class="my-1">${renderInlineMd(line)}</p>`);
  }
  flushList();
  return out.join('\n');
}

function renderInlineMd(s: string): string {
  // Process links [text](url) first; everything else escape as text
  const linkRe = /\[([^\]]*)\]\((https?:\/\/[^)\s]+)\)/g;
  let out = '';
  let idx = 0;
  let m: RegExpExecArray | null;
  while ((m = linkRe.exec(s)) !== null) {
    out += escapeHtml(s.slice(idx, m.index));
    const text = escapeHtml(m[1]);
    const href = escapeHtml(m[2]);
    out += `<a href="${href}" target="_blank" rel="noopener noreferrer" class="text-violet-600 hover:underline">${text}</a>`;
    idx = m.index + m[0].length;
  }
  out += escapeHtml(s.slice(idx));
  return out;
}

// Stylise a unified diff — color-code - and + lines.
function formatDiff(diff: string): ReactNode {
  return diff.split(/\r?\n/).map((line, i) => {
    let cls = 'text-gray-600';
    if (line.startsWith('+ ')) cls = 'text-emerald-700 bg-emerald-50';
    else if (line.startsWith('- ')) cls = 'text-red-700 bg-red-50';
    else if (line.startsWith('--- ') || line.startsWith('+++ ')) cls = 'text-gray-500 font-semibold';
    return (
      <div key={i} className={`${cls} px-2 -mx-2`}>
        {line || '\u00A0'}
      </div>
    );
  });
}
